import type { Context } from 'hono'
import { Readable } from 'node:stream'
import { iconForMime, type CollectionEntityDefinition, type ModuleContext } from '@gzl10/nexus-sdk'
import { useIdField, useTextField, useTextUniqueField, useSelectField, useUrlField, useNumberField, usePasswordField, useMetadataField, useDatetimeField } from '@gzl10/nexus-sdk/fields'
import { getStorageService } from './storage.service.js'
import { getImageProcessingService } from './image-processing.service.js'
import { checkFileAccess } from './actions/helpers.js'
import { getVideoProcessingService, isVideoMimetype, VIDEO_MIMETYPES_LIST } from './video-processing.service.js'
import { resolveVideoFile, setVideoResultHeaders, type VideoFileRecord } from './video.helpers.js'

/** Convert a Node Readable to a Web ReadableStream for Hono response bodies. */
function toWebStream(stream: NodeJS.ReadableStream): ReadableStream<Uint8Array> {
  return Readable.toWeb(stream as Readable) as unknown as ReadableStream<Uint8Array>
}

// ============================================================================
// CONFIG ENTITY (Collection — multi-scope, envDefaults for 'default')
// ============================================================================

const DEFAULT_MAX_SIZE = 10 * 1024 * 1024 // 10MB

/** FieldCondition: hidden when driver ≠ filesystem */
const hiddenIfNotFs = { field: 'driver', $ne: 'filesystem' }
/** FieldCondition: hidden when driver ≠ s3 */
const hiddenIfNotS3 = { field: 'driver', $ne: 's3' }

/**
 * Config Entity: storage_configs
 *
 * Collection entity — each row is a storage scope (e.g., 'default', 's3-cdn').
 * The 'default' scope is auto-seeded from env vars via envDefaults.
 * Additional scopes can be created via CRUD (UI/API).
 */
export const storageConfigEntity: CollectionEntityDefinition = {
  type: 'collection',
  table: 'storage_configs',
  label: { en: 'Storage Config', es: 'Configuración Storage' },
  labelPlural: { en: 'Storage Configs', es: 'Configuraciones Storage' },
  labelField: 'scope',
  routePrefix: '/config',
  timestamps: true,
  order: 3,

  envDefaults: {
    match: { scope: 'default' },
    fields: {
      driver: { env: 'STORAGE_DRIVER' },
      base_url: { env: 'STORAGE_URL' },
      max_file_size: { env: 'STORAGE_MAX_SIZE', parse: 'number' },
      allowed_mime_types: { env: 'STORAGE_ALLOWED_TYPES' },
      base_path: { env: 'STORAGE_PATH' },
      bucket: { env: 'S3_BUCKET' },
      region: { env: 'S3_REGION' },
      access_key_id: { env: 'S3_ACCESS_KEY' },
      secret_access_key: { env: 'S3_SECRET_KEY' },
      endpoint: { env: 'S3_ENDPOINT' },
      resize_max_width: { env: 'STORAGE_RESIZE_MAX_WIDTH', parse: 'number' },
      resize_max_height: { env: 'STORAGE_RESIZE_MAX_HEIGHT', parse: 'number' },
      resize_quality: { env: 'STORAGE_RESIZE_QUALITY', parse: 'number' },
      video_max_width: { env: 'STORAGE_VIDEO_MAX_WIDTH', parse: 'number' },
      video_max_height: { env: 'STORAGE_VIDEO_MAX_HEIGHT', parse: 'number' },
      video_quality: { env: 'STORAGE_VIDEO_QUALITY', parse: 'number' },
      video_max_preview: { env: 'STORAGE_VIDEO_MAX_PREVIEW', parse: 'number' },
      video_max_transcode: { env: 'STORAGE_VIDEO_MAX_TRANSCODE', parse: 'number' }
    }
  },

  fields: {
    id: useIdField(),
    scope: useTextUniqueField({
      label: { en: 'Scope', es: 'Ámbito' },
      size: 100,
      hint: { en: 'Unique identifier for this config (e.g., default, s3-cdn)', es: 'Identificador único para esta config (ej: default, s3-cdn)' }
    }),
    driver: {
      ...useSelectField({
        label: { en: 'Driver', es: 'Controlador' },
        required: true,
        defaultValue: 'filesystem',
        hint: { en: 'Storage backend', es: 'Backend de almacenamiento' },
        options: [
          { value: 'filesystem', label: { en: 'Filesystem (local)', es: 'Sistema de archivos (local)' } },
          { value: 's3', label: { en: 'S3 (AWS/MinIO)', es: 'S3 (AWS/MinIO)' } }
        ]
      }),
      validation: { enum: ['filesystem', 's3'] }
    },
    base_url: useUrlField({
      label: { en: 'Base URL', es: 'URL base' },
      hint: { en: 'Public URL to generate file links', es: 'URL pública para generar enlaces a archivos' },
      size: 500,
      nullable: true
    }),
    max_file_size: useNumberField({
      label: { en: 'Max File Size (bytes)', es: 'Tamaño máximo de archivo (bytes)' },
      hint: { en: 'Maximum size in bytes (default: 10MB = 10485760)', es: 'Tamaño máximo en bytes (default: 10MB = 10485760)' },
      defaultValue: DEFAULT_MAX_SIZE,
      nullable: false,
      displayProps: { format: 'bytes' }
    }),
    allowed_mime_types: useTextField({
      label: { en: 'Allowed MIME Types', es: 'Tipos MIME permitidos' },
      hint: { en: 'Comma-separated (e.g. image/*,application/pdf)', es: 'Separados por coma (ej: image/*,application/pdf)' },
      size: 1000,
      nullable: true
    }),

    // ── Filesystem fields ──────────────────────────────────────────
    base_path: {
      ...useTextField({
        label: { en: 'Base Path', es: 'Ruta base' },
        hint: { en: 'Absolute or relative path (e.g. ./storage)', es: 'Ruta absoluta o relativa (ej: ./storage)' },
        size: 500,
        nullable: true
      }),
      hidden: hiddenIfNotFs
    },

    // ── S3 fields ──────────────────────────────────────────────────
    bucket: {
      ...useTextField({
        label: { en: 'Bucket', es: 'Bucket' },
        size: 200,
        nullable: true
      }),
      hidden: hiddenIfNotS3
    },
    region: {
      ...useTextField({
        label: { en: 'Region', es: 'Región' },
        hint: { en: 'e.g. us-east-1', es: 'ej: us-east-1' },
        size: 100,
        nullable: true
      }),
      hidden: hiddenIfNotS3
    },
    access_key_id: {
      ...useTextField({
        label: { en: 'Access Key ID', es: 'ID de clave de acceso' },
        size: 200,
        nullable: true
      }),
      hidden: hiddenIfNotS3
    },
    secret_access_key: {
      ...usePasswordField({
        label: { en: 'Secret Access Key', es: 'Clave secreta de acceso' },
        size: 500,
        nullable: true
      }),
      hidden: hiddenIfNotS3
    },
    endpoint: {
      ...useUrlField({
        label: { en: 'Custom Endpoint', es: 'Endpoint personalizado' },
        hint: { en: 'For MinIO or other S3-compatible providers', es: 'Para MinIO u otros proveedores compatibles con S3' },
        size: 500,
        nullable: true
      }),
      hidden: hiddenIfNotS3
    },

    // ── Resize fields ─────────────────────────────────────────────
    resize_max_width: useNumberField({
      label: { en: 'Resize Max Width', es: 'Ancho máximo resize' },
      hint: { en: 'Default: 2048', es: 'Por defecto: 2048' },
      defaultValue: 2048,
      nullable: true
    }),
    resize_max_height: useNumberField({
      label: { en: 'Resize Max Height', es: 'Alto máximo resize' },
      hint: { en: 'Default: 2048', es: 'Por defecto: 2048' },
      defaultValue: 2048,
      nullable: true
    }),
    resize_quality: useNumberField({
      label: { en: 'Resize Quality', es: 'Calidad resize' },
      hint: { en: 'JPEG/WebP quality 1-100 (default: 80)', es: 'Calidad JPEG/WebP 1-100 (por defecto: 80)' },
      defaultValue: 80,
      nullable: true
    }),

    // ── Video fields ──────────────────────────────────────────────
    video_max_width: useNumberField({
      label: { en: 'Video Max Width', es: 'Ancho máximo video' },
      hint: { en: 'Default: 1280', es: 'Por defecto: 1280' },
      defaultValue: 1280,
      nullable: true
    }),
    video_max_height: useNumberField({
      label: { en: 'Video Max Height', es: 'Alto máximo video' },
      hint: { en: 'Default: 720', es: 'Por defecto: 720' },
      defaultValue: 720,
      nullable: true
    }),
    video_quality: useNumberField({
      label: { en: 'Video Quality', es: 'Calidad video' },
      hint: { en: 'JPEG quality for thumbnails 1-100 (default: 80)', es: 'Calidad JPEG para miniaturas 1-100 (por defecto: 80)' },
      defaultValue: 80,
      nullable: true
    }),
    video_max_preview: useNumberField({
      label: { en: 'Video Max Preview (s)', es: 'Máx. duración preview (s)' },
      hint: { en: 'Max preview duration in seconds (default: 5)', es: 'Duración máxima del preview en segundos (por defecto: 5)' },
      defaultValue: 5,
      nullable: true
    }),
    video_max_transcode: useNumberField({
      label: { en: 'Video Max Transcode (bytes)', es: 'Máx. transcode (bytes)' },
      hint: { en: 'Max file size for transcode (default: 500MB)', es: 'Tamaño máximo para transcode (por defecto: 500MB)' },
      defaultValue: 500 * 1024 * 1024,
      nullable: true,
      displayProps: { format: 'bytes' }
    })
  },

  casl: {
    subject: 'StorageConfig',
    sensitiveFields: ['secret_access_key', 'access_key_id'],
    permissions: {
      ADMIN: { actions: ['create', 'read', 'update', 'delete'] },
      SUPPORT: { actions: ['read'] }
    }
  }
}

// ============================================================================
// COLLECTION ENTITY
// ============================================================================

/**
 * EntityDefinition for Storage Files
 *
 * Stores metadata for files uploaded to the system.
 * Physical files are stored in the configured driver (filesystem/S3).
 */

/** FieldCondition: visible only for video mimetypes */
const videoOnly = { field: 'mimetype', $in: [...VIDEO_MIMETYPES_LIST] }

export const storageFilesEntity: CollectionEntityDefinition = {
  type: "collection",
  realtime: "sync",
  taggeable: true,
  table: "storage_files",
  label: { en: "Files", es: "Archivos" },
  labelPlural: { en: "Files", es: "Archivos" },
  labelField: "filename",
  timestamps: true,
  audit: true,
  routePrefix: "/files",
  allowCreate: false, // Files are created via upload action only
  allowEdit: false,
  defaultSort: { field: "created_at", order: "desc" },
  availableDisplayModes: ["table", "board", "list", "masonry"],
  groupableFields: ["visibility", "folder", "mimetype"],
  order: 1,

  // Inject a virtual `icon` field derived from `mimetype` on every read.
  // Not persisted in DB — purely a presentation helper for the UI.
  hooks: () => {
    const withIcon = <T extends { mimetype?: string | null } | null>(record: T) =>
      record ? { ...record, icon: iconForMime(record.mimetype) } : record
    return {
      afterFindById: (record) => withIcon(record as any) as any,
      afterFindAll: (result) => ({ ...result, items: result.items.map(r => withIcon(r as any)) as any })
    }
  },

  actions: [
    {
      key: "download",
      label: { en: "Download File", es: "Descargar archivo" },
      icon: "mdi:download",
      method: "GET",
      select: [
        "id",
        "filename",
        "mimetype",
        "size",
        "visibility",
        "created_by",
      ],
      skipAuth: true,
      handler: async (ctx: ModuleContext, input: unknown, c?: Context<any>) => {
        if (!c) throw new ctx.core.errors.AppError("Hono Context required", 500);

        const { _record: file } = input as {
          _record: {
            id: string;
            filename: string;
            mimetype: string;
            size: number;
            visibility: string;
          };
        };

        checkFileAccess(file, c, ctx);

        const storageService = getStorageService();
        const stream = await storageService.getStream(file.id);

        c.header("Content-Type", file.mimetype);
        c.header(
          "Content-Disposition",
          `attachment; filename="${encodeURIComponent(file.filename)}"`,
        );
        c.header("Content-Length", file.size.toString());
        if (file.visibility === "public") {
          c.header("Cache-Control", "public, max-age=86400");
        } else if (file.visibility === "internal") {
          c.header("Cache-Control", "private, max-age=3600");
        } else {
          c.header("Cache-Control", "private, no-cache");
        }

        return c.body(toWebStream(stream));
      },
    },
    {
      key: "inline",
      label: { en: "View File", es: "Ver archivo" },
      icon: "mdi:eye",
      method: "GET",
      select: [
        "id",
        "filename",
        "mimetype",
        "size",
        "visibility",
        "hash",
        "created_by",
      ],
      skipAuth: true,
      handler: async (ctx: ModuleContext, input: unknown, c?: Context<any>) => {
        if (!c) throw new ctx.core.errors.AppError("Hono Context required", 500);

        const { _record: file } = input as {
          _record: {
            id: string;
            filename: string;
            mimetype: string;
            size: number;
            visibility: string;
            hash?: string;
          };
        };

        checkFileAccess(file, c, ctx);

        const storageService = getStorageService();
        const imageService = getImageProcessingService();

        // Parse resize options from query params
        const query = c.req.query();
        const accept = c.req.header("accept");
        const resizeOpts = imageService.parseOptions(query, accept);

        // Dynamic resize for images
        if (resizeOpts && file.mimetype.startsWith("image/")) {
          const etag = imageService.buildETag(file.hash, resizeOpts);
          if (c.req.header("if-none-match") === etag) {
            return c.body(null, 304);
          }

          const buffer = await storageService.getBuffer(file.id);
          const result = await imageService.resize(file.id, buffer, resizeOpts);

          c.header("Content-Type", result.mimetype);
          c.header(
            "Content-Disposition",
            `inline; filename="${encodeURIComponent(file.filename.replace(/\.[^.]+$/, "." + result.format))}"`,
          );
          c.header("Content-Length", result.buffer.length.toString());
          c.header("Cache-Control", "public, max-age=86400");
          c.header("ETag", etag);
          c.header("Vary", "Accept");
          return c.body(result.buffer as unknown as ArrayBuffer);
        }

        // Non-resize: stream original file
        const stream = await storageService.getStream(file.id);

        c.header("Content-Type", file.mimetype);
        c.header(
          "Content-Disposition",
          `inline; filename="${encodeURIComponent(file.filename)}"`,
        );
        c.header("Content-Length", file.size.toString());
        if (file.visibility === "public") {
          c.header("Cache-Control", "public, max-age=86400");
        } else if (file.visibility === "internal") {
          c.header("Cache-Control", "private, max-age=3600");
        } else {
          c.header("Cache-Control", "private, no-cache");
        }
        return c.body(toWebStream(stream));
      },
    },
    {
      key: "thumbnail",
      label: { en: "Video Thumbnail", es: "Miniatura de video" },
      icon: "mdi:image-frame",
      method: "GET",
      visible: videoOnly,
      select: [
        "id",
        "filename",
        "mimetype",
        "size",
        "visibility",
        "hash",
        "created_by",
      ],
      skipAuth: true,
      handler: async (ctx: ModuleContext, input: unknown, c?: Context<any>) => {
        if (!c) throw new ctx.core.errors.AppError("Hono Context required", 500);
        const { _record: file } = input as { _record: VideoFileRecord };

        const videoService = getVideoProcessingService();
        const query = c.req.query();
        const opts = videoService.parseThumbnailOptions({
          ...query,
          frame: query["frame"] || "0",
        });
        if (!opts)
          throw new ctx.core.errors.ValidationError(
            "Invalid thumbnail options",
          );

        const etag = videoService.buildETag(
          file.hash,
          "thumb",
          opts as unknown as Record<string, unknown>,
        );
        if (c.req.header("if-none-match") === etag) {
          return c.body(null, 304);
        }

        const resolved = await resolveVideoFile(file, c, ctx);
        try {
          const result = await videoService.extractThumbnail(
            resolved.path,
            file.id,
            opts,
          );
          setVideoResultHeaders(c, file, result, etag);
          return c.body(result.buffer as unknown as ArrayBuffer);
        } finally {
          await resolved.cleanup();
        }
      },
    },
    {
      key: "preview",
      label: { en: "Video Preview", es: "Vista previa de video" },
      icon: "mdi:play-circle-outline",
      method: "GET",
      visible: videoOnly,
      select: [
        "id",
        "filename",
        "mimetype",
        "size",
        "visibility",
        "hash",
        "created_by",
      ],
      skipAuth: true,
      handler: async (ctx: ModuleContext, input: unknown, c?: Context<any>) => {
        if (!c) throw new ctx.core.errors.AppError("Hono Context required", 500);
        const { _record: file } = input as { _record: VideoFileRecord };

        const videoService = getVideoProcessingService();
        const query = c.req.query();
        const opts = videoService.parsePreviewOptions({
          ...query,
          preview: query["preview"] || "3s",
        });
        if (!opts)
          throw new ctx.core.errors.ValidationError("Invalid preview options");

        const etag = videoService.buildETag(
          file.hash,
          "preview",
          opts as unknown as Record<string, unknown>,
        );
        if (c.req.header("if-none-match") === etag) {
          return c.body(null, 304);
        }

        const resolved = await resolveVideoFile(file, c, ctx);
        try {
          const result = await videoService.generatePreview(
            resolved.path,
            file.id,
            opts,
          );
          setVideoResultHeaders(c, file, result, etag);
          return c.body(result.buffer as unknown as ArrayBuffer);
        } finally {
          await resolved.cleanup();
        }
      },
    },
    {
      key: "metadata",
      label: { en: "Video Metadata", es: "Metadatos de video" },
      icon: "mdi:information-outline",
      method: "GET",
      visible: videoOnly,
      select: [
        "id",
        "filename",
        "mimetype",
        "size",
        "visibility",
        "hash",
        "created_by",
        "metadata",
      ],
      skipAuth: true,
      handler: async (ctx: ModuleContext, input: unknown, c?: Context<any>) => {
        if (!c) throw new ctx.core.errors.AppError("Hono Context required", 500);
        const { _record: file } = input as {
          _record: VideoFileRecord & { metadata?: string | null };
        };

        checkFileAccess(file, c, ctx);
        if (!isVideoMimetype(file.mimetype))
          throw new ctx.core.errors.ValidationError("Not a video file");

        // Return cached metadata from DB if available
        if (file.metadata) {
          try {
            const cached =
              typeof file.metadata === "string"
                ? JSON.parse(file.metadata)
                : file.metadata;
            if (cached.duration !== undefined) {
              c.header("Cache-Control", "public, max-age=86400");
              return c.json(cached);
            }
          } catch {
            /* fall through to ffprobe */
          }
        }

        const videoService = getVideoProcessingService();
        if (!(await videoService.isAvailable())) {
          throw new ctx.core.errors.AppError(
            "Video processing not available (ffmpeg required)",
            503,
          );
        }

        const storageService = getStorageService();
        const resolved = await storageService.resolveInputFile(file.id);
        try {
          const meta = await videoService.getMetadata(resolved.path);

          // Persist for future requests (fire-and-forget)
          ctx.db
            .knex("storage_files")
            .where({ id: file.id })
            .update({ metadata: JSON.stringify(meta) })
            .catch((err) =>
              ctx.core.logger.warn(
                { err, fileId: file.id },
                "Failed to persist video metadata",
              ),
            );

          c.header("Cache-Control", "public, max-age=86400");
          return c.json(meta);
        } finally {
          await resolved.cleanup();
        }
      },
    },
    {
      key: "stream",
      label: { en: "Stream File", es: "Reproducir archivo" },
      icon: "mdi:play",
      method: "GET",
      visible: videoOnly,
      select: [
        "id",
        "filename",
        "mimetype",
        "size",
        "visibility",
        "created_by",
      ],
      skipAuth: true,
      handler: async (ctx: ModuleContext, input: unknown, c?: Context<any>) => {
        if (!c) throw new ctx.core.errors.AppError("Hono Context required", 500);
        const { _record: file } = input as { _record: VideoFileRecord };

        checkFileAccess(file, c, ctx);
        const storageService = getStorageService();
        const range = c.req.header("range");

        if (range) {
          const parts = range.replace(/bytes=/, "").split("-");
          const start = parseInt(parts[0]!, 10);
          const end = parts[1] ? parseInt(parts[1], 10) : file.size - 1;
          const chunkSize = end - start + 1;

          const { stream } = await storageService.getStreamRange(
            file.id,
            start,
            end,
          );

          c.header("Content-Range", `bytes ${start}-${end}/${file.size}`);
          c.header("Accept-Ranges", "bytes");
          c.header("Content-Length", chunkSize.toString());
          c.header("Content-Type", file.mimetype);
          c.header("Cache-Control", "public, max-age=3600");
          return c.body(toWebStream(stream as NodeJS.ReadableStream), 206);
        }

        const stream = await storageService.getStream(file.id);
        c.header("Content-Type", file.mimetype);
        c.header("Content-Length", file.size.toString());
        c.header("Accept-Ranges", "bytes");
        c.header("Cache-Control", "public, max-age=3600");
        return c.body(toWebStream(stream));
      },
    },
    {
      key: "transcode",
      label: { en: "Transcode Video", es: "Transcodificar video" },
      icon: "mdi:movie-open-settings",
      input: {
        format: {
          ...useSelectField({
            label: { en: "Target Format", es: "Formato destino" },
            options: [
              { value: "mp4", label: { en: "MP4 (H.264)", es: "MP4 (H.264)" } },
              { value: "webm", label: { en: "WebM (VP9)", es: "WebM (VP9)" } },
            ],
          }),
          validation: { enum: ["mp4", "webm"] },
        },
        quality: useSelectField({
          label: { en: "Quality", es: "Calidad" },
          options: [
            { value: "low", label: { en: "Low", es: "Baja" } },
            { value: "medium", label: { en: "Medium", es: "Media" } },
            { value: "high", label: { en: "High", es: "Alta" } },
          ],
          defaultValue: "medium",
        }),
      },
      select: ["id", "mimetype"],
      visible: videoOnly,
      handler: async (ctx: ModuleContext, input: unknown) => {
        const {
          _record: file,
          format,
          quality,
        } = input as {
          _record: { id: string; mimetype: string };
          format: string;
          quality?: string;
        };

        if (!isVideoMimetype(file.mimetype))
          throw new ctx.core.errors.ValidationError("Not a video file");

        const schedulesService = ctx.services.getOptional<any>("scheduler");
        if (!schedulesService?.runFunction) {
          throw new ctx.core.errors.AppError(
            "Transcode requires the schedules module for async processing",
            503,
          );
        }

        await schedulesService.runFunction("storage.transcode", {
          fileId: file.id,
          format,
          quality: quality || "medium",
        });

        return { message: "Transcode queued" };
      },
    },
  ],

  fields: {
    id: useIdField(),
    icon: {
      input: 'icon',
      label: { en: 'Icon', es: 'Icono' },
      db: { type: 'string', virtual: true, nullable: true },
      meta: { sortable: false, searchable: false, exportable: false },
      displayProps: { align: 'center', width: 56 }
    },
    filename: useTextField({
      label: { en: "Original Filename", es: "Nombre original" },
      required: true,
      size: 255,
      nullable: false,
      meta: { sortable: true, searchable: true },
    }),
    disk_filename: useTextField({
      label: { en: "Disk Filename", es: "Nombre en disco" },
      required: true,
      hidden: true,
      size: 255,
      nullable: false,
    }),
    mimetype: useTextField({
      label: { en: "MIME Type", es: "Tipo MIME" },
      required: true,
      size: 100,
      nullable: false,
      index: true,
      meta: { sortable: true, searchable: true, showInDisplay: false },
    }),
    size: useNumberField({
      label: { en: "Size", es: "Tamaño" },
      required: true,
      nullable: false,
      validation: { min: 0 },
      meta: { sortable: true },
      displayProps: { format: "bytes" },
    }),
    folder: useTextField({
      label: { en: "Folder", es: "Carpeta" },
      size: 100,
      nullable: true,
      index: true,
      meta: { searchable: true },
    }),
    scope: useTextField({
      label: { en: "Storage Scope", es: "Ámbito de almacenamiento" },
      hidden: true,
      size: 50,
      nullable: true,
      index: true,
      hint: {
        en: "Storage configuration scope used for this file",
        es: "Ámbito de configuración de almacenamiento usado para este archivo",
      },
    }),
    path: useTextField({
      label: { en: "Full Path", es: "Ruta completa" },
      required: true,
      hidden: true,
      size: 500,
      nullable: false,
    }),
    url: useUrlField({
      label: { en: "Public URL", es: "URL pública" },
      size: 500,
      nullable: true,
      meta: { exportable: true, showInDisplay: false },
    }),
    hash: useTextField({
      label: { en: "SHA256 Hash", es: "Hash SHA256" },
      hidden: true,
      size: 64,
      nullable: true,
      index: true,
    }),
    visibility: useSelectField({
      label: { en: "Visibility", es: "Visibilidad" },
      options: [
        { value: "public", label: { en: "Public", es: "Público" }, icon: "mdi:earth" },
        { value: "internal", label: { en: "Internal", es: "Interno" }, icon: "mdi:lock-open-outline" },
        { value: "private", label: { en: "Private", es: "Privado" }, icon: "mdi:lock" },
      ],
      defaultValue: "internal",
      displayProps: { align: 'center' }
    }),
    metadata: useMetadataField(),
    created_at: {
      ...useDatetimeField({
        label: { en: "Uploaded", es: "Subido" },
        disabled: true,
        nullable: false,
        meta: { sortable: true },
      }),
      db: { type: 'datetime', nullable: false, defaultFn: 'now' }
    },
  },

  indexes: [{ columns: ["folder", "filename"] }],

  casl: {
    subject: "StorageFile",
    permissions: {
      "*": { actions: ["read"], conditions: { visibility: "public" } },
      "@": { actions: ["read"], conditions: { visibility: "internal" } },
      EDITOR: { actions: ["create", "read", "delete"] },
      USER: {
        actions: ["create", "read", "delete"],
        conditions: { created_by: "${user.id}" },
      },
    },
  },
};

// NOTE: Module-scope actions (upload) are in ./actions/
// NOTE: Row-scope actions (download, inline) are in storageFilesEntity.actions[]
